import { useState } from 'react';
import { Paperclip, Star } from 'lucide-react';
import type { EventType } from '@casetrace/shared';
import { createInitialFilterState, hasActiveFilters } from './filter-panel-helpers';
import { FilterPanelPeriod } from './filter-panel-period';
import { FilterPanelTypeSection } from './filter-panel-type-section';

export type ConditionField = 'sender' | 'text';

export interface FilterCondition {
  field: ConditionField;
  value: string;
}

export interface FilterState {
  types: Set<EventType>;
  dateFrom: string;
  dateTo: string;
  hasAttachments: boolean;
  onlyStarred: boolean;
  include: FilterCondition[];
  exclude: FilterCondition[];
}

interface FilterPanelProps {
  filters: FilterState;
  counts: Partial<Record<EventType, number>>;
  attachmentCount: number;
  starredCount: number;
  onChange: (filters: FilterState) => void;
}

interface AttachmentToggleProps {
  active: boolean;
  count: number;
  onToggle: () => void;
}

interface StarredToggleProps {
  active: boolean;
  count: number;
  onToggle: () => void;
}

function StarredToggle({ active, count, onToggle }: StarredToggleProps) {
  const color = '#f5b301';
  return (
    <button
      onClick={onToggle}
      className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors duration-100"
      style={
        active
          ? {
              background: `${color}22`,
              outline: `1px solid ${color}70`,
            }
          : {
              background: 'rgba(255,255,255,0.04)',
              outline: '1px solid rgba(255,255,255,0.06)',
            }
      }
    >
      <span
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded"
        style={{ background: active ? `${color}50` : `${color}30` }}
      >
        <Star size={11} style={{ color }} fill={active ? color : 'none'} />
      </span>
      <span
        className="flex-1 truncate text-[11px] font-medium"
        style={{ color: active ? color : '#dde0e8' }}
      >
        Kun relevante
      </span>
      {count > 0 && (
        <span
          className="shrink-0 rounded px-1 py-0.5 text-[10px] font-semibold"
          style={
            active
              ? { background: `${color}35`, color }
              : { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }
          }
        >
          {count}
        </span>
      )}
    </button>
  );
}

function AttachmentToggle({ active, count, onToggle }: AttachmentToggleProps) {
  const color = '#f97316';
  return (
    <button
      onClick={onToggle}
      className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors duration-100"
      style={
        active
          ? {
              background: `${color}22`,
              outline: `1px solid ${color}70`,
            }
          : {
              background: 'rgba(255,255,255,0.04)',
              outline: '1px solid rgba(255,255,255,0.06)',
            }
      }
    >
      <span
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded"
        style={{ background: active ? `${color}50` : `${color}30` }}
      >
        <Paperclip size={11} style={{ color }} />
      </span>
      <span
        className="flex-1 truncate text-[11px] font-medium"
        style={{ color: active ? color : '#dde0e8' }}
      >
        Har vedlegg
      </span>
      {count > 0 && (
        <span
          className="shrink-0 rounded px-1 py-0.5 text-[10px] font-semibold"
          style={
            active
              ? { background: `${color}35`, color }
              : { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }
          }
        >
          {count}
        </span>
      )}
    </button>
  );
}

export function FilterPanel({
  filters,
  counts,
  attachmentCount,
  starredCount,
  onChange,
}: FilterPanelProps) {
  const [expanded, setExpanded] = useState(true);

  function toggleType(type: EventType) {
    const next = new Set(filters.types);
    if (next.has(type)) next.delete(type);
    else next.add(type);
    onChange({ ...filters, types: next });
  }

  const total = Object.values(counts).reduce((s, n) => s + (n ?? 0), 0);
  const activeFilters = hasActiveFilters(filters);

  return (
    <aside
      className="flex w-48 shrink-0 flex-col gap-0 overflow-y-auto border-r py-4"
      style={{ borderColor: 'rgba(255,255,255,0.07)' }}
    >
      <div className="flex items-center justify-between px-4 pb-3">
        <span className="text-xs font-medium text-text-primary">
          Filtre{total > 0 && <span className="ml-1 text-text-secondary">({total})</span>}
        </span>
        {activeFilters && (
          <button
            onClick={() => onChange(createInitialFilterState())}
            className="text-[10px] text-accent hover:text-accent-hover"
          >
            Nullstill
          </button>
        )}
      </div>

      <FilterPanelTypeSection
        filters={filters}
        counts={counts}
        expanded={expanded}
        onToggleExpanded={() => setExpanded(!expanded)}
        onToggleType={toggleType}
      />

      <div className="flex flex-col gap-0.5 px-2 pb-3">
        <StarredToggle
          active={filters.onlyStarred}
          count={starredCount}
          onToggle={() => onChange({ ...filters, onlyStarred: !filters.onlyStarred })}
        />
        <AttachmentToggle
          active={filters.hasAttachments}
          count={attachmentCount}
          onToggle={() => onChange({ ...filters, hasAttachments: !filters.hasAttachments })}
        />
      </div>

      <FilterPanelPeriod filters={filters} />
    </aside>
  );
}
